import { memo, useEffect, useMemo, useRef, useState } from "react";
import { Stack } from "@mantine/core";
import { UserMessage } from "../user-message";
import {} from "../types";
import { SpeechConfig, V2ChatHistoryTurn, V2Source } from "../../api";
import { ResponseComponent } from "../response";
import { SttRequestIds } from "../stt-request-ids";
import { ChatErrorKind } from "../../utils/chatError";

export interface ConversationProps {
    hasError: boolean;
    errorKind?: ChatErrorKind;
    errorMessage?: string;
    isLoading: boolean;
    isStreaming: boolean;
    streamingAnswer?: V2ChatHistoryTurn;
    answers: V2ChatHistoryTurn[];
    onSourceClick: (source: V2Source, anchor: string) => void;
    onFollowupQuestionClick: (question: string) => void;
    // Admin viewers: show each question's speech-to-text request ids under it.
    showSttRequestIds?: boolean;
}

interface AnswerListProps {
    turns: V2ChatHistoryTurn[];
    onSourceClick: (source: V2Source, anchor: string) => void;
    onFollowupQuestionClick: (question: string) => void;
    speechConfig: SpeechConfig;
    showSttRequestIds?: boolean;
}

const AnswersList = memo(({ turns: turns, onSourceClick, onFollowupQuestionClick, speechConfig, showSttRequestIds }: AnswerListProps) => {
    const lastAnswerIndex = turns.length - 1;

    return (
        <>
            {turns.map((answer, index) => {
                return (
                    // Key by conversation id + index so switching conversations remounts the
                    // turn components instead of reusing instances (and their internal state)
                    // from the previous conversation at the same positions.
                    <Stack key={`${answer.response?.id ?? "turn"}-${index}`} gap="lg">
                        <Stack gap={4}>
                            <UserMessage message={answer.userMessage} />
                            {showSttRequestIds && <SttRequestIds ids={answer.sstRequestIds} />}
                        </Stack>
                        {answer.response && (
                            <ResponseComponent
                                index={index}
                                isLoading={false}
                                response={answer.response}
                                onSourceClick={onSourceClick}
                                onFollowupQuestionClick={onFollowupQuestionClick}
                                speechConfig={speechConfig}
                                showFeedback={lastAnswerIndex === index}
                            />
                        )}
                    </Stack>
                );
            })}
        </>
    );
});

export const Conversation = ({
    hasError,
    errorKind,
    errorMessage,
    isLoading,
    isStreaming,
    streamingAnswer,
    answers,
    onSourceClick,
    onFollowupQuestionClick,
    showSttRequestIds
}: ConversationProps) => {
    const [speechUrls, setSpeechUrls] = useState<(string | null)[]>([]);
    const audio = useMemo(() => new Audio(), []);
    const [isPlaying, setIsPlaying] = useState(false);

    // Speech URLs are stored per turn index. When the conversation changes (e.g. a
    // history item is selected without this component unmounting), drop them so the
    // previous conversation's audio is not associated with the new turns.
    const conversationId = answers[0]?.response?.id ?? null;
    useEffect(() => {
        setSpeechUrls([]);
        audio.pause();
        setIsPlaying(false);
    }, [conversationId]);

    // When a new turn starts, anchor its question to the top of the scroll viewport
    // and then leave scrolling alone: auto-scrolling to the container bottom while
    // streaming would show the citation list (rendered below the growing answer text)
    // instead of the answer, and would fight the user's own scrolling.
    const streamingTurnRef = useRef<HTMLDivElement>(null);
    const streamingQuestion = streamingAnswer?.userMessage;
    useEffect(() => {
        if (streamingQuestion) {
            streamingTurnRef.current?.scrollIntoView({ block: "start", behavior: "auto" });
        }
    }, [streamingQuestion]);

    // Stable identity so AnswersList's memo is effective while an answer streams.
    const speechConfig: SpeechConfig = useMemo(
        () => ({
            speechUrls,
            setSpeechUrls,
            audio,
            isPlaying,
            setIsPlaying
        }),
        [speechUrls, audio, isPlaying]
    );

    return (
        <Stack gap="lg">
            <AnswersList
                turns={answers}
                onSourceClick={onSourceClick}
                onFollowupQuestionClick={onFollowupQuestionClick}
                speechConfig={speechConfig}
                showSttRequestIds={showSttRequestIds}
            />
            {(isStreaming || isLoading || hasError) && streamingAnswer && (
                <div ref={streamingTurnRef} style={{ scrollMarginTop: 8 }}>
                    <Stack gap="lg">
                        <UserMessage message={streamingAnswer.userMessage} />
                        <ResponseComponent
                            hasError={hasError}
                            errorKind={errorKind}
                            errorMessage={errorMessage}
                            isLoading={isLoading}
                            response={streamingAnswer.response}
                            onSourceClick={onSourceClick}
                            onFollowupQuestionClick={onFollowupQuestionClick}
                            speechConfig={speechConfig}
                            showFeedback={false}
                        />
                    </Stack>
                </div>
            )}
        </Stack>
    );
};
